// The Inbox's Memory lane (RECALL-INBOX-LOOP; design 2026-10-31 recall-full-integration §4.2):
// one list for everything memory wants a person's yes or no on, lessons and page candidates from
// agents, dream and rule proposals, Recall's settle suggestions and contradictions, and the "did
// it work" checks on decisions. Every card answers in place (approve / edit / reject; small
// things in a modal). A card whose answer the memory service cannot take yet shows "waiting on
// memory service" and is kept (and sent later). Contradictions are settled one by one, never in
// bulk. The owner/CEO switch ("See all agents' memory") lives behind an (i) popover.
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldLabel,
  InfoTip,
  Notice,
  Textarea,
  toast,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckIcon, ClockIcon, PencilIcon, XIcon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { invalidateMemory, type LaneItem, type LaneKind, memoryLaneQuery } from "./memory-queries";
import { AcceptDialog, ConflictCard, RejectPromotionDialog } from "./memory-review-screen";

const KIND_LABEL: Record<LaneKind, string> = {
  lesson: "Lesson",
  candidate: "Page",
  dream: "Tidy-up",
  rule: "Rule",
  settle: "To settle",
  contradiction: "Conflict",
  outcome: "Did it work?",
  archive: "Archive these",
};

const KIND_INFO: Record<LaneKind, string> = {
  lesson: "Something an agent learned. Approve it and it joins the team's memory.",
  candidate: "A note worth keeping as a page. Pick where it lives.",
  dream: "Memory suggests merging or retiring old notes. Nothing changes until you approve.",
  rule: "A rule an agent wants to follow from now on.",
  settle: "Two notes disagree and memory has a suggestion. You decide.",
  contradiction: "Two notes disagree. You decide; these are never approved in bulk.",
  outcome: "You made this decision a while ago. Tell us how it turned out.",
  archive:
    "Notes that no longer match how the company is set up. Approve to archive them; reject to keep them. Never approved in bulk.",
};

const WHY_LABEL: Record<LaneItem["why_here"], string> = {
  yours: "Your department",
  served: "From an agent you work with",
  escalated: "Came up to you",
  owner_all: "Another team's",
};

const WHY_INFO: Record<LaneItem["why_here"], string> = {
  yours: "It is in your department's memory, so you are the one who decides.",
  served: "It came from an agent that works for you.",
  escalated:
    "Its department has no person to decide, or nobody answered in time, so it came up to the owner.",
  owner_all:
    "You see it because you switched on all agents' memory. Personal spaces are never included.",
};

type Decide = {
  action: "approve" | "reject" | "edit_approve" | "worked" | "did_not" | "too_early";
  text?: string;
  /** MD-STATUS-FIX: a lesson digest's answers, lesson by lesson. */
  items?: { write_id: string; keep: boolean; text?: string }[];
};

export function MemoryLane({
  api,
  company,
  focus = null,
  embedded = false,
}: {
  api: Api;
  company: CompanyAccess;
  /** `{kind}:{id}` of the card an Inbox row pointed at: brought into view and marked. */
  focus?: string | null;
  /** Inside another page (Setup › Memory › Review): no scroller of its own, no padding. */
  embedded?: boolean;
}) {
  const qc = useQueryClient();
  const lane = useQuery(memoryLaneQuery(api, company.id));
  const loaded = lane.data !== undefined;
  useEffect(() => {
    if (!focus || !loaded) return;
    [...document.querySelectorAll("[data-lane-card]")]
      .find((el) => el.getAttribute("data-lane-card") === focus)
      ?.scrollIntoView?.({ block: "center" });
  }, [focus, loaded]);
  const bulk = useMutation({
    mutationFn: () => api.client.memory.laneBulk({ params: { co: company.id }, body: {} }),
    onSuccess: (r) => {
      toast.add({
        title: r.approved ? `Approved ${r.approved}` : "Nothing to approve",
        description: [
          r.waiting ? `${r.waiting} waiting on the memory service` : null,
          r.skipped ? `${r.skipped} left for you` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        type: "success",
      });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not approved. Try again."), type: "error" }),
    onSettled: () => invalidateMemory(qc, company.id),
  });
  if (lane.isPending) return <Loading label="Loading memory" />;
  if (lane.isError) return <LoadError what="Memory" error={lane.error} />;
  const d = lane.data;
  const bulkable = d.items.filter((x) => x.state === "open" && x.can_bulk).length;
  return (
    <div
      data-slot="memory-lane"
      className={
        embedded
          ? "flex min-w-0 flex-col gap-3"
          : "flex min-w-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="font-heading text-sm font-semibold">Memory</h2>
        <InfoTip label="About the Memory lane">
          What your agents and memory want a yes or no on. Approve, edit or reject right here.
        </InfoTip>
        <span className="ml-auto flex flex-wrap items-center gap-2">
          {d.see_all.available ? (
            <SeeAllSwitch api={api} company={company} on={d.see_all.on} />
          ) : null}
          <Button
            size="sm"
            variant="outline"
            disabled={!bulkable || bulk.isPending}
            onClick={() => bulk.mutate()}
            data-slot="lane-bulk"
          >
            <CheckIcon aria-hidden="true" />
            Approve all ({bulkable})
          </Button>
          <InfoTip label="About approve all">
            Approves every lesson, tidy-up and rule here. Conflicts, page candidates and checks stay
            for you.
          </InfoTip>
        </span>
      </div>
      {d.waiting ? (
        <Notice tone="info" data-slot="lane-waiting">
          {d.waiting} {d.waiting === 1 ? "answer is" : "answers are"} waiting on the memory service.
          They are kept and sent once it can take them.
        </Notice>
      ) : null}
      {d.items.length === 0 ? (
        <EmptyState title="All clear" description="Nothing in memory needs you right now." />
      ) : (
        <ul className="flex min-w-0 flex-col gap-3">
          {d.items.map((x) => (
            <li
              key={x.id}
              data-lane-card={x.id}
              data-focused={x.id === focus ? "true" : undefined}
              className={x.id === focus ? "rounded-lg ring-2 ring-ring" : undefined}
            >
              <LaneCard api={api} company={company} item={x} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The owner/CEO switch: every agent's memory, never personal spaces; each change is audited. */
export function SeeAllSwitch({
  api,
  company,
  on,
}: {
  api: Api;
  company: CompanyAccess;
  on: boolean;
}) {
  const qc = useQueryClient();
  const set = useMutation({
    mutationFn: (next: boolean) =>
      api.client.memory.laneSeeAll({ params: { co: company.id }, body: { on: next } }),
    onSuccess: (r) =>
      toast.add({
        title: r.on ? "Showing all agents' memory" : "Showing yours only",
        type: "success",
      }),
    onError: (e) => toast.add({ title: problemText(e, "Not changed. Try again."), type: "error" }),
    onSettled: () => invalidateMemory(qc, company.id),
  });
  const id = useId();
  return (
    <span className="flex items-center gap-1.5" data-slot="lane-see-all">
      <input
        id={id}
        type="checkbox"
        role="switch"
        aria-checked={on}
        checked={on}
        disabled={set.isPending}
        onChange={(e) => set.mutate(e.target.checked)}
        className="size-4 accent-primary"
      />
      <label htmlFor={id} className="text-xs font-medium">
        See all agents' memory
      </label>
      <InfoTip label="About seeing all agents' memory">
        Owner and CEO only. Off by default. Adds other teams' agents' items to this list. Personal
        spaces are never included. Turning it on or off is recorded.
      </InfoTip>
    </span>
  );
}

function LaneCard({ api, company, item: x }: { api: Api; company: CompanyAccess; item: LaneItem }) {
  if (x.kind === "settle" || x.kind === "contradiction") {
    return x.conflict ? (
      <div data-lane-item={x.id} className="flex flex-col gap-1">
        {/* the card below names the space itself: once is enough */}
        <CardHead item={x} showScope={false} />
        <ConflictCard api={api} company={company} conflict={x.conflict} />
      </div>
    ) : null;
  }
  return <PlainCard api={api} company={company} item={x} />;
}

function CardHead({ item: x, showScope = true }: { item: LaneItem; showScope?: boolean }) {
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <Badge variant="accent">{KIND_LABEL[x.kind]}</Badge>
      <InfoTip label={`About ${KIND_LABEL[x.kind]}`}>{KIND_INFO[x.kind]}</InfoTip>
      {showScope && x.scope_name ? <Badge variant="outline">{x.scope_name}</Badge> : null}
      {x.why_here !== "yours" ? (
        <Badge variant="outline" data-slot="lane-why">
          {WHY_LABEL[x.why_here]}
          <InfoTip label="Why it is here">{WHY_INFO[x.why_here]}</InfoTip>
        </Badge>
      ) : null}
      {x.from ? <span className="text-xs text-muted-foreground">from {x.from}</span> : null}
      {x.since ? (
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {formatDateTime(x.since)}
        </span>
      ) : null}
    </div>
  );
}

function PlainCard({
  api,
  company,
  item: x,
}: {
  api: Api;
  company: CompanyAccess;
  item: LaneItem;
}) {
  const qc = useQueryClient();
  const decide = useMutation({
    mutationFn: (a: Decide) =>
      api.client.memory.laneDecide({
        params: { co: company.id },
        body: { kind: x.kind, ref_id: x.ref_id, ...a, ui_event_id: crypto.randomUUID() },
      }),
    onSuccess: (r) =>
      toast.add({
        title: r.result === "waiting" ? "Kept: waiting on memory service" : "Done",
        type: "success",
      }),
    onError: (e) => toast.add({ title: problemText(e, "Not saved. Try again."), type: "error" }),
    onSettled: () => invalidateMemory(qc, company.id),
  });
  const waiting = x.state === "waiting";
  return (
    <Card className="flex min-w-0 flex-col gap-3 p-4" data-lane-item={x.id} role="article">
      <CardHead item={x} />
      <p className="text-sm font-medium break-words">{x.title}</p>
      {x.detail ? <p className="text-sm text-muted-foreground break-words">{x.detail}</p> : null}
      {x.outcome ? (
        <p className="text-xs text-muted-foreground">
          Decided {formatDateTime(x.outcome.decided_on)}
          {x.outcome.expected ? ` · hoped for: ${x.outcome.expected}` : ""}
        </p>
      ) : null}
      {waiting ? (
        <p
          data-slot="lane-card-waiting"
          className="flex items-center gap-1.5 text-xs text-muted-foreground"
        >
          <ClockIcon aria-hidden="true" className="size-3.5" />
          Waiting on memory service
          <InfoTip label="About waiting">
            {x.waiting_note ?? "Your answer is kept and sent when the memory service can take it."}
          </InfoTip>
        </p>
      ) : !x.can_decide ? (
        <span className="text-xs text-muted-foreground">A person who reviews this decides.</span>
      ) : x.kind === "outcome" ? (
        <span className="flex flex-wrap gap-1.5" data-slot="lane-actions">
          <Button
            size="sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: "worked" })}
          >
            <CheckIcon aria-hidden="true" />
            Worked
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: "did_not" })}
          >
            <XIcon aria-hidden="true" />
            Did not
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: "too_early" })}
          >
            Too early
          </Button>
        </span>
      ) : x.kind === "lesson" && x.lessons && x.lessons.length > 0 ? (
        <DigestAnswers
          item={x}
          lessons={x.lessons}
          pending={decide.isPending}
          onDecide={(a) => decide.mutate(a)}
        />
      ) : x.kind === "candidate" && x.promotion ? (
        <span className="flex flex-wrap gap-1.5" data-slot="lane-actions">
          <AcceptDialog api={api} company={company} promotion={x.promotion} />
          <RejectPromotionDialog api={api} company={company} promotion={x.promotion} />
        </span>
      ) : (
        <span className="flex flex-wrap gap-1.5" data-slot="lane-actions">
          <Button
            size="sm"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: "approve" })}
            aria-label={`Approve: ${x.title}`}
          >
            <CheckIcon aria-hidden="true" />
            Approve
          </Button>
          {x.can_edit ? (
            <EditDialog
              item={x}
              pending={decide.isPending}
              onSave={(text) => decide.mutate({ action: "edit_approve", text })}
            />
          ) : null}
          <Button
            size="sm"
            variant="outline"
            disabled={decide.isPending}
            onClick={() => decide.mutate({ action: "reject" })}
            aria-label={`Reject: ${x.title}`}
          >
            <XIcon aria-hidden="true" />
            Reject
          </Button>
        </span>
      )}
    </Card>
  );
}

type DigestLesson = NonNullable<LaneItem["lessons"]>[number];

/**
 * MD-STATUS-FIX: a day's lesson digest, answered lesson by lesson: keep or reject each one, edit
 * its words, then save; or approve or reject them all at once.
 */
function DigestAnswers({
  item: x,
  lessons,
  pending,
  onDecide,
}: {
  item: LaneItem;
  lessons: DigestLesson[];
  pending: boolean;
  onDecide: (a: Decide) => void;
}) {
  const [keep, setKeep] = useState<Record<string, boolean>>({});
  const [text, setText] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const touched = Object.keys(keep).length + Object.keys(text).length > 0;
  const kept = lessons.filter((l) => keep[l.write_id] !== false).length;
  // Codex r1: a kept lesson whose words were cleared is not saved as the original
  const blank = lessons.some(
    (l) =>
      keep[l.write_id] !== false && text[l.write_id] !== undefined && !text[l.write_id]?.trim(),
  );
  const save = () =>
    onDecide({
      action: "edit_approve",
      items: lessons.map((l) => {
        const t = text[l.write_id]?.trim();
        return {
          write_id: l.write_id,
          keep: keep[l.write_id] !== false,
          ...(t && t !== l.sentence ? { text: t } : {}),
        };
      }),
    });
  return (
    <div className="flex flex-col gap-3" data-slot="lane-digest">
      <ol className="flex flex-col gap-2">
        {lessons.map((l, n) => {
          const on = keep[l.write_id] !== false;
          return (
            <li
              key={l.write_id}
              data-digest-lesson={l.write_id}
              data-keep={on ? "true" : "false"}
              className="flex flex-col gap-1.5 rounded-md border border-subtle p-2"
            >
              <div className="flex items-start gap-2">
                <span className="text-xs text-muted-foreground tabular-nums">{n + 1}.</span>
                {editing === l.write_id ? (
                  <Textarea
                    aria-label={`Lesson ${n + 1}`}
                    value={text[l.write_id] ?? l.sentence}
                    maxLength={2000}
                    rows={2}
                    onChange={(e) => setText({ ...text, [l.write_id]: e.target.value })}
                  />
                ) : (
                  <p className={on ? "text-sm break-words" : "text-sm break-words line-through"}>
                    {text[l.write_id] ?? l.sentence}
                    {l.repeats > 0 ? (
                      <span className="ml-1 text-xs text-muted-foreground">
                        (said {l.repeats + 1} times)
                      </span>
                    ) : null}
                  </p>
                )}
              </div>
              {x.can_edit ? (
                <span className="flex flex-wrap gap-1.5">
                  <Button
                    size="xs"
                    variant={on ? "secondary" : "outline"}
                    disabled={pending}
                    aria-pressed={on}
                    onClick={() => setKeep({ ...keep, [l.write_id]: !on })}
                  >
                    {on ? <CheckIcon aria-hidden="true" /> : <XIcon aria-hidden="true" />}
                    {on ? "Keep" : "Rejected"}
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    disabled={pending}
                    onClick={() => setEditing(editing === l.write_id ? null : l.write_id)}
                  >
                    <PencilIcon aria-hidden="true" />
                    {editing === l.write_id ? "Done" : "Edit"}
                  </Button>
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      <span className="flex flex-wrap gap-1.5" data-slot="lane-actions">
        {touched ? (
          <Button
            size="sm"
            disabled={pending || blank}
            title={blank ? "Write the lesson, or reject it" : undefined}
            onClick={save}
          >
            <CheckIcon aria-hidden="true" />
            Save: keep {kept} of {lessons.length}
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={pending}
            onClick={() => onDecide({ action: "approve" })}
            aria-label={`Approve all: ${x.title}`}
          >
            <CheckIcon aria-hidden="true" />
            Approve all
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          disabled={pending}
          onClick={() => onDecide({ action: "reject" })}
          aria-label={`Reject all: ${x.title}`}
        >
          <XIcon aria-hidden="true" />
          Reject all
        </Button>
      </span>
    </div>
  );
}

/** Edit: approve it in the person's own words (a small modal; the page stays put). */
function EditDialog({
  item: x,
  pending,
  onSave,
}: {
  item: LaneItem;
  pending: boolean;
  onSave: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);
  // starts from the whole proposed text; the title is only its short name
  const [text, setText] = useState(x.proposed_text ?? x.title);
  const id = useId();
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        size="sm"
        variant="outline"
        disabled={pending}
        onClick={() => setOpen(true)}
        aria-label={`Edit: ${x.title}`}
      >
        <PencilIcon aria-hidden="true" />
        Edit
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit and approve</DialogTitle>
          <DialogDescription>Your words are what memory keeps.</DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={id}>What to keep</FieldLabel>
          <Textarea
            id={id}
            value={text}
            maxLength={2000}
            onChange={(e) => setText(e.target.value)}
          />
        </Field>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" />}>Cancel</DialogClose>
          <Button
            disabled={!text.trim() || pending}
            onClick={() => {
              onSave(text.trim());
              setOpen(false);
            }}
          >
            Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
